import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, Plus, Trash2, RefreshCw, X, FileUp, HelpCircle, Zap, Download, Send } from "lucide-react";
import { apiClient } from "../../App";

const ENTITLEMENTS = ["base", "shorts", "studio"];

// Minimal RFC-4180 CSV parser. Handles quoted fields with commas + escaped
// double-quotes ("a,b" → a,b ; "say ""hi""" → say "hi"). Returns array of
// arrays. Empty trailing rows are dropped.
function parseCSV(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field); field = "";
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = "";
      if (row.some((v) => v.length > 0)) rows.push(row);
      row = [];
    } else {
      field += c;
    }
  }
  if (field.length || row.length) {
    row.push(field);
    if (row.some((v) => v.length > 0)) rows.push(row);
  }
  return rows;
}

// Map a parsed CSV (array of arrays, first row = header) into BuyerImportRow[].
function csvToBuyers(text) {
  const rows = parseCSV(text);
  if (rows.length < 2) return { buyers: [], errors: ["CSV is empty or has no data rows"] };
  const header = rows[0].map((h) => h.trim().replace(/^\uFEFF/, ""));  // strip BOM
  const idx = (name) => header.findIndex((h) => h.toLowerCase() === name.toLowerCase());

  const emailIdx = idx("email");
  if (emailIdx === -1) {
    return {
      buyers: [],
      errors: ["CSV must include an 'email' column. Other columns are optional."],
    };
  }

  const splitList = (s) => (s || "").split(/[,;|]/).map((x) => x.trim()).filter(Boolean);
  const parseNum = (s) => {
    const n = parseInt((s || "0").replace(/[^0-9-]/g, ""), 10);
    return Number.isFinite(n) ? n : 0;
  };
  const parseDate = (s) => {
    if (!s || !s.trim()) return null;
    const d = new Date(s.trim());
    return isNaN(d.getTime()) ? s.trim() : d.toISOString();
  };

  const cell = (row, col) => {
    const i = idx(col);
    return i === -1 ? "" : (row[i] || "").trim();
  };

  const buyers = rows.slice(1).map((row) => ({
    email: cell(row, "email").toLowerCase(),
    entitlements: splitList(cell(row, "entitlements")),
    totalSpendCents: parseNum(cell(row, "totalSpendCents") || cell(row, "spend") || cell(row, "total_spend_cents")),
    seenOrderIds: splitList(cell(row, "seenOrderIds") || cell(row, "order_ids")),
    orderId: cell(row, "orderId") || cell(row, "order_id") || null,
    addedAt: parseDate(cell(row, "addedAt") || cell(row, "added_at") || cell(row, "createdAt") || cell(row, "created_at")),
    lastLoginAt: parseDate(cell(row, "lastLoginAt") || cell(row, "last_login_at") || cell(row, "lastLogin")),
    loginCount: parseNum(cell(row, "loginCount") || cell(row, "login_count")),
    scriptCount: parseNum(cell(row, "scriptCount") || cell(row, "script_count")),
    shortsCount: parseNum(cell(row, "shortsCount") || cell(row, "shorts_count")),
    firstUseAt: parseDate(cell(row, "firstUseAt") || cell(row, "first_use_at")),
    source: cell(row, "source") || "csv-import",
    event: cell(row, "event") || null,
  }));
  return { buyers, errors: [] };
}

function fmtCents(c) {
  if (!c) return "$0";
  return `$${(c / 100).toFixed(2)}`;
}
function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return s;
  }
}

export default function BuyersTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [entitlementFilter, setEntitlementFilter] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [toast, setToast] = useState(null);
  const [granting, setGranting] = useState(null); // buyer email currently choosing entitlement
  const [importing, setImporting] = useState(false);
  const [csvHelp, setCsvHelp] = useState(false);
  // Result of the most recent "Test webhook" click — shown inline below the
  // toolbar so the admin can read the diagnostic without losing context.
  const [webhookTest, setWebhookTest] = useState(null);
  const [webhookTesting, setWebhookTesting] = useState(false);
  // GHL connection status — { configured, url_host, auth_header_set } from
  // /admin/ghl/status. Pill in the toolbar shows green/amber based on this.
  const [ghlStatus, setGhlStatus] = useState(null);
  const [ghlTesting, setGhlTesting] = useState(false);
  const [ghlPushing, setGhlPushing] = useState(null); // email currently pushing
  const csvFileRef = useRef(null);
  const [addModal, setAddModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newEnts, setNewEnts] = useState(() => new Set(["base"]));
  const [adding, setAdding] = useState(false);

  const showToast = useCallback((msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (entitlementFilter) params.entitlement = entitlementFilter;
      const r = await apiClient.get("/admin/buyers", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Failed to load buyers", "err");
    } finally {
      setLoading(false);
    }
  }, [q, entitlementFilter, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // GHL connection status — one-shot fetch on mount, then re-fetched after
  // a successful test push so the pill reflects the latest reality.
  useEffect(() => {
    let cancelled = false;
    apiClient.get("/admin/ghl/status").then((r) => {
      if (!cancelled) setGhlStatus(r.data || null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // GHL sentinel push — sends a sentinel payload to verify the webhook
  // is wired without affecting any buyer record.
  const runGhlTest = async () => {
    if (ghlTesting) return;
    setGhlTesting(true);
    try {
      const r = await apiClient.post("/admin/ghl/test", {});
      const ok = r.data?.result?.status === "ok";
      showToast(ok ? "GHL test push succeeded" : `GHL push returned ${r.data?.result?.http_status || r.data?.result?.status}`, ok ? "ok" : "err");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "GHL test failed";
      showToast(typeof detail === "string" ? detail : "GHL test failed", "err");
    } finally {
      setGhlTesting(false);
    }
  };

  // Per-buyer manual replay — used when a buyer landed via a path that
  // didn't fire GHL (legacy import, transient outage, manual create).
  const pushBuyerToGhl = async (email) => {
    if (ghlPushing) return;
    setGhlPushing(email);
    try {
      const r = await apiClient.post("/admin/ghl/push-buyer", { email });
      const ok = r.data?.result?.status === "ok";
      showToast(ok ? `Pushed ${email} to GHL` : `GHL push for ${email} returned ${r.data?.result?.http_status || r.data?.result?.status}`, ok ? "ok" : "err");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "GHL push failed";
      showToast(typeof detail === "string" ? detail : "GHL push failed", "err");
    } finally {
      setGhlPushing(null);
    }
  };

  // Silent auto-refresh so newly-arrived webhook buyers appear on the
  // Admin Buyers tab without the admin having to click Refresh manually.
  // Polls every 20s while the tab is visible — pauses when the browser
  // tab is hidden (Page Visibility API) so we don't burn the DB on tabs
  // left open overnight. Per Charity's 2026-02-23 feedback: "if they
  // purchase and it's not showing in admin, that's an issue."
  useEffect(() => {
    let stop = false;
    const tick = () => {
      if (stop) return;
      if (typeof document !== "undefined" && document.hidden) return;
      load();
    };
    const id = setInterval(tick, 20_000);
    // Also refresh the moment the tab regains focus — covers the "tab
    // was hidden when webhook fired" case immediately.
    const onVis = () => {
      if (!document.hidden) load();
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVis);
    }
    return () => {
      stop = true;
      clearInterval(id);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVis);
      }
    };
  }, [load]);

  const allSelected = useMemo(
    () => items.length > 0 && items.every((b) => selected.has(b.email)),
    [items, selected],
  );
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map((b) => b.email)));
  };
  const toggleOne = (email) => {
    const next = new Set(selected);
    if (next.has(email)) next.delete(email);
    else next.add(email);
    setSelected(next);
  };

  const grant = async (email, ent) => {
    // Optimistic update
    const prev = items;
    setItems((cur) =>
      cur.map((b) =>
        b.email === email
          ? { ...b, entitlements: Array.from(new Set([...(b.entitlements || []), ent])) }
          : b,
      ),
    );
    setGranting(null);
    try {
      await apiClient.patch(`/admin/buyers/${encodeURIComponent(email)}/grant`, { entitlement: ent });
      showToast(`Granted ${ent} to ${email}`);
    } catch (e) {
      setItems(prev);
      showToast(e?.response?.data?.detail || "Grant failed", "err");
    }
  };

  const revoke = async (email, ent) => {
    const prev = items;
    setItems((cur) =>
      cur.map((b) =>
        b.email === email
          ? { ...b, entitlements: (b.entitlements || []).filter((x) => x !== ent) }
          : b,
      ),
    );
    try {
      await apiClient.patch(`/admin/buyers/${encodeURIComponent(email)}/revoke`, { entitlement: ent });
      showToast(`Revoked ${ent} from ${email}`);
    } catch (e) {
      setItems(prev);
      showToast(e?.response?.data?.detail || "Revoke failed", "err");
    }
  };

  const deleteOne = async (email) => {
    if (!window.confirm(`Delete buyer ${email}? This cannot be undone.`)) return;
    const prev = items;
    setItems((cur) => cur.filter((b) => b.email !== email));
    try {
      await apiClient.delete(`/admin/buyers/${encodeURIComponent(email)}`);
      showToast(`Deleted ${email}`);
    } catch (e) {
      setItems(prev);
      showToast(e?.response?.data?.detail || "Delete failed", "err");
    }
  };

  const bulkDelete = async () => {
    const emails = Array.from(selected);
    if (emails.length === 0) return;
    if (!window.confirm(`Delete ${emails.length} buyer(s)? This cannot be undone.`)) return;
    const prev = items;
    setItems((cur) => cur.filter((b) => !selected.has(b.email)));
    setSelected(new Set());
    try {
      const r = await apiClient.post("/admin/buyers/bulk-delete", { emails });
      showToast(`Deleted ${r.data.deleted} buyers`);
    } catch (e) {
      setItems(prev);
      showToast(e?.response?.data?.detail || "Bulk delete failed", "err");
    }
  };

  // Test the Pinball webhook end-to-end. Calls our own /admin/pinball/test-webhook
  // endpoint which builds a synthetic payload and processes it through the
  // SAME _process_pinball_event helper the live Pinball webhook uses. Lets
  // the admin verify connectivity + grant logic without bothering a real
  // customer or triggering a paid test order. Result + synthetic test email
  // is shown inline so the admin can immediately delete the test buyer.
  const runWebhookTest = async () => {
    if (webhookTesting) return;
    setWebhookTesting(true);
    setWebhookTest(null);
    try {
      const r = await apiClient.post("/admin/pinball/test-webhook", {});
      setWebhookTest({ ok: r.data.ok !== false, ...r.data });
      if (r.data.ok !== false) {
        showToast("Webhook test passed — synthetic buyer created");
        load();  // refresh table so the synthetic buyer shows up
      } else {
        showToast(`Webhook test failed: ${r.data.detail || "unknown"}`, "err");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Webhook test failed";
      setWebhookTest({ ok: false, detail });
      showToast(detail, "err");
    } finally {
      setWebhookTesting(false);
    }
  };

  const importFromCSV = async (file) => {
    if (!file || importing) return;
    setImporting(true);
    try {
      const text = await file.text();
      const { buyers, errors } = csvToBuyers(text);
      if (errors.length) {
        showToast(errors[0], "err");
        return;
      }
      if (buyers.length === 0) {
        showToast("No rows found in CSV", "err");
        return;
      }
      const r = await apiClient.post("/admin/buyers/import", { buyers });
      const { imported, merged, skipped, errors: rowErrors } = r.data;
      showToast(
        `Import done — ${imported} new · ${merged} merged · ${skipped} skipped` +
          (rowErrors?.length ? ` · ${rowErrors.length} errors` : ""),
      );
      load();
    } catch (e) {
      showToast(e?.response?.data?.detail || e?.message || "CSV import failed", "err");
    } finally {
      setImporting(false);
      if (csvFileRef.current) csvFileRef.current.value = "";
    }
  };

  // Download the buyers list as CSV. Backend streams the file with a
  // filename header (F2F48-buyers-YYYY-MM-DD-export.csv); we use the
  // browser's anchor-download trick rather than apiClient.get so the
  // file lands in the user's downloads folder directly instead of
  // showing up as a JSON-stringified blob in the network tab.
  const [exporting, setExporting] = useState(false);
  const downloadBuyersCSV = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const r = await apiClient.get("/admin/buyers/export", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `F2F48-buyers-${today}-export.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast("Buyers CSV downloaded");
    } catch (e) {
      showToast(e?.response?.data?.detail || e?.message || "CSV export failed", "err");
    } finally {
      setExporting(false);
    }
  };

  const openAddBuyer = () => {
    setNewEmail("");
    setNewEnts(new Set(["base"]));
    setAddModal(true);
  };

  const submitAddBuyer = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      showToast("Enter a valid email address", "err");
      return;
    }
    if (newEnts.size === 0) {
      showToast("Pick at least one entitlement", "err");
      return;
    }
    setAdding(true);
    try {
      // Grant each entitlement sequentially. The grant endpoint upserts,
      // so the first call creates the buyer record and subsequent calls
      // attach additional entitlements.
      for (const ent of newEnts) {
        await apiClient.patch(
          `/admin/buyers/${encodeURIComponent(email)}/grant`,
          { entitlement: ent },
        );
      }
      showToast(`Added ${email} with ${Array.from(newEnts).join(", ")}`);
      setAddModal(false);
      load();
    } catch (e) {
      showToast(e?.response?.data?.detail || "Failed to add buyer", "err");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="admin-section" data-testid="buyers-tab">
      <div className="admin-toolbar">
        <div className="admin-search">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search by email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="buyers-search"
          />
        </div>
        <select
          className="admin-select"
          value={entitlementFilter}
          onChange={(e) => setEntitlementFilter(e.target.value)}
          data-testid="buyers-filter-entitlement"
        >
          <option value="">All entitlements</option>
          {ENTITLEMENTS.map((e) => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
        <button className="admin-btn" onClick={load} data-testid="buyers-refresh">
          <RefreshCw size={13} /> Refresh
        </button>
        <button
          className="admin-btn is-primary"
          onClick={openAddBuyer}
          data-testid="buyers-add"
          title="Manually add a buyer email with entitlements"
        >
          <Plus size={13} /> Add buyer
        </button>

        {/* Hidden file input drives the CSV import button */}
        <input
          ref={csvFileRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={(e) => importFromCSV(e.target.files?.[0])}
          data-testid="buyers-csv-input"
        />
        <button
          className="admin-btn is-primary"
          onClick={() => csvFileRef.current?.click()}
          disabled={importing}
          data-testid="buyers-import-csv"
          title="Upload a CSV exported from Netlify, GHL, Pinball, or anywhere else"
        >
          <FileUp size={13} /> {importing ? "Importing…" : "Import CSV"}
        </button>
        <button
          className="admin-btn"
          onClick={downloadBuyersCSV}
          disabled={exporting}
          data-testid="buyers-export-csv"
          title="Download every buyer as a CSV (F2F48-buyers-YYYY-MM-DD-export.csv)"
        >
          <Download size={13} /> {exporting ? "Exporting…" : "Export CSV"}
        </button>
        <button
          className="admin-btn"
          onClick={() => setCsvHelp(true)}
          aria-label="CSV format help"
          data-testid="buyers-csv-help"
          title="Show CSV format help"
        >
          <HelpCircle size={13} />
        </button>
        <button
          className="admin-btn"
          onClick={runWebhookTest}
          disabled={webhookTesting}
          data-testid="buyers-test-webhook"
          title="Send a synthetic Pinball payload to your own webhook endpoint and verify the grant logic. Creates a disposable test buyer you can delete."
        >
          <Zap size={13} /> {webhookTesting ? "Testing…" : "Test webhook"}
        </button>
        {/* GHL connection status pill + sentinel-test trigger. Anchored
            to the right edge of the toolbar so the layout stays compact —
            avoids the "Test GHL" button orphaning to a second row when
            GHL is configured (iter_37 polish). */}
        <span
          className={`admin-pill ${ghlStatus?.configured ? "is-ok" : "is-warn"}`}
          data-testid="ghl-status-pill"
          style={{ marginLeft: "auto" }}
          title={
            ghlStatus?.configured
              ? `GHL connected → ${ghlStatus.url_host || "webhook"}${ghlStatus.auth_header_set ? " · auth header set" : ""}`
              : "GHL not configured. Set GHL_WEBHOOK_URL in backend/.env and restart."
          }
        >
          GHL: {ghlStatus?.configured ? "connected" : "off"}
        </span>
        <button
          className="admin-btn"
          onClick={runGhlTest}
          disabled={ghlTesting || !ghlStatus?.configured}
          data-testid="ghl-test-button"
          title={ghlStatus?.configured
            ? "Send a sentinel payload to your GHL webhook to verify it fires."
            : "Configure GHL_WEBHOOK_URL first."
          }
        >
          <Send size={13} /> {ghlTesting ? "Testing…" : "Test GHL"}
        </button>
        {selected.size > 0 && (
          <button
            className="admin-btn is-danger"
            onClick={bulkDelete}
            data-testid="buyers-bulk-delete"
          >
            <Trash2 size={13} /> Delete {selected.size}
          </button>
        )}
        <span className="admin-meta" data-testid="buyers-total">{total} total</span>
      </div>

      {webhookTest && (
        <div
          className={`admin-banner ${webhookTest.ok ? "is-ok" : "is-err"}`}
          data-testid="buyers-webhook-test-result"
        >
          <div className="admin-banner-body">
            <strong>{webhookTest.ok ? "✓ Webhook healthy" : "✗ Webhook test failed"}</strong>
            <div style={{ marginTop: 4, fontSize: 13 }}>
              {webhookTest.ok
                ? webhookTest.message
                : `${webhookTest.detail || "Unknown error"}${webhookTest.status_code ? ` (HTTP ${webhookTest.status_code})` : ""}`}
            </div>
            {webhookTest.ok && webhookTest.test_email && (
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.75 }}>
                Synthetic buyer: <code>{webhookTest.test_email}</code> · entitlement: <code>{webhookTest.test_product}</code>
              </div>
            )}
          </div>
          <button
            className="admin-banner-close"
            onClick={() => setWebhookTest(null)}
            aria-label="Dismiss"
            data-testid="buyers-webhook-test-dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table" data-testid="buyers-table">
          <thead>
            <tr>
              <th className="admin-th-checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Select all"
                  data-testid="buyers-select-all"
                />
              </th>
              <th>Email</th>
              <th>Entitlements</th>
              <th>Spend</th>
              <th>Last login</th>
              <th>Added</th>
              <th className="admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="admin-empty">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="admin-empty">No buyers found. Click <strong>Import CSV</strong> to seed the list.</td></tr>
            )}
            {!loading && items.map((b) => (
              <tr key={b.email} data-testid={`buyer-row-${b.email}`}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(b.email)}
                    onChange={() => toggleOne(b.email)}
                    aria-label={`Select ${b.email}`}
                    data-testid={`buyer-select-${b.email}`}
                  />
                </td>
                <td className="admin-td-email">{b.email}</td>
                <td>
                  <div className="ent-chips">
                    {(b.entitlements || []).map((ent) => (
                      <span key={ent} className={`ent-chip ent-chip-${ent}`}>
                        {ent}
                        <button
                          className="ent-chip-x"
                          onClick={() => revoke(b.email, ent)}
                          aria-label={`Revoke ${ent}`}
                          data-testid={`revoke-${b.email}-${ent}`}
                        >
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                    {granting === b.email ? (
                      <div className="ent-grant-pop">
                        {ENTITLEMENTS.filter((e) => !(b.entitlements || []).includes(e)).map((e) => (
                          <button
                            key={e}
                            className="ent-grant-opt"
                            onClick={() => grant(b.email, e)}
                            data-testid={`grant-${b.email}-${e}`}
                          >
                            + {e}
                          </button>
                        ))}
                        <button className="ent-grant-cancel" onClick={() => setGranting(null)}>
                          <X size={10} />
                        </button>
                      </div>
                    ) : (
                      (b.entitlements || []).length < ENTITLEMENTS.length && (
                        <button
                          className="ent-grant-btn"
                          onClick={() => setGranting(b.email)}
                          data-testid={`grant-open-${b.email}`}
                        >
                          <Plus size={10} /> Grant
                        </button>
                      )
                    )}
                  </div>
                </td>
                <td>{fmtCents(b.totalSpendCents)}</td>
                <td>{fmtDate(b.lastLoginAt)}</td>
                <td>{fmtDate(b.addedAt)}</td>
                <td>
                  {ghlStatus?.configured && (
                    <button
                      className="admin-icon-btn"
                      onClick={() => pushBuyerToGhl(b.email)}
                      disabled={ghlPushing === b.email}
                      aria-label="Push to GHL"
                      title="Replay this buyer's GHL contact + tier push (no-op if already in your workflow)"
                      data-testid={`ghl-push-${b.email}`}
                      style={{ marginRight: 6 }}
                    >
                      <Send size={13} />
                    </button>
                  )}
                  <button
                    className="admin-icon-btn"
                    onClick={() => deleteOne(b.email)}
                    aria-label="Delete buyer"
                    data-testid={`delete-${b.email}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {addModal && (
        <div className="admin-confirm-overlay" data-testid="add-buyer-overlay" onClick={() => !adding && setAddModal(false)}>
          <div className="admin-confirm-card add-buyer-card" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: "var(--accent)" }}><Plus size={20} /> Add buyer manually</h3>
            <p>
              Use this when someone bought outside the funnel (refund replacement, manual comp, support exception).
              The webhook still works for normal purchases — this is just the emergency lever.
            </p>
            <label className="add-buyer-field">
              <span>Email</span>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="buyer@example.com"
                autoFocus
                data-testid="add-buyer-email"
              />
            </label>
            <div className="add-buyer-field">
              <span>Entitlements</span>
              <div className="add-buyer-ents">
                {ENTITLEMENTS.map((ent) => (
                  <label key={ent} className={`ent-pick ${newEnts.has(ent) ? "is-on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={newEnts.has(ent)}
                      onChange={(e) => {
                        const next = new Set(newEnts);
                        if (e.target.checked) next.add(ent);
                        else next.delete(ent);
                        setNewEnts(next);
                      }}
                      data-testid={`add-buyer-ent-${ent}`}
                    />
                    <span className={`ent-chip ent-chip-${ent}`}>{ent}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="admin-confirm-actions">
              <button className="admin-btn" onClick={() => setAddModal(false)} disabled={adding} data-testid="add-buyer-cancel">
                Cancel
              </button>
              <button className="admin-btn is-primary" onClick={submitAddBuyer} disabled={adding} data-testid="add-buyer-submit">
                {adding ? "Adding…" : "Add buyer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {csvHelp && (
        <div className="admin-confirm-overlay" data-testid="csv-help-overlay" onClick={() => setCsvHelp(false)}>
          <div className="admin-confirm-card csv-help-card" onClick={(e) => e.stopPropagation()}>
            <h3><HelpCircle size={20} /> CSV format</h3>
            <p>
              First row is the header. Only <code>email</code> is required — every other column is optional.
              Unknown columns are ignored, so you can drop in exports from Netlify, GHL, or Pinball as-is.
            </p>
            <pre className="csv-help-example">{`email,entitlements,totalSpendCents,seenOrderIds,addedAt,lastLoginAt,loginCount
alex@example.com,"base,studio",29700,"po_1,po_2",2026-01-01,2026-02-15,12
jamie@example.com,base,700,po_3,2026-02-10,2026-02-18,4
priya@example.com,"base,shorts",4400,,,,`}</pre>
            <ul className="csv-help-notes">
              <li><strong>entitlements</strong> &amp; <strong>seenOrderIds</strong>: comma/semicolon/pipe-separated inside quotes.</li>
              <li><strong>Counter columns</strong> (totalSpendCents, loginCount, scriptCount, shortsCount): integers; existing values are kept if higher.</li>
              <li><strong>Date columns</strong> (addedAt, lastLoginAt, firstUseAt): any parseable date; leave blank to preserve existing.</li>
              <li>Existing buyers are <strong>upserted</strong>: entitlements + order IDs are unioned, counters take the max, addedAt keeps the earliest, lastLoginAt keeps the latest. Never null-overwrites.</li>
              <li>Bad rows (invalid email, etc.) are skipped and reported back in the result toast.</li>
            </ul>
            <div className="admin-confirm-actions">
              <button className="admin-btn is-primary" onClick={() => setCsvHelp(false)} data-testid="csv-help-close">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`admin-toast is-${toast.kind}`} data-testid="admin-toast" role="status">
          {toast.msg}
        </div>
      )}
    </div>
  );
}
